import {type FC, useState} from "react";

import type {StepWizardChildProps} from "react-step-wizard";

import {WizardNavigationButtons} from "./WizardNavigationButtons";
import {generateProductList, resetReportState, updatePlan} from "../../actions";
import {useAppDispatch, useAppSelector} from "../../hooks";
import type {
  MeterSize,
  PlanState,
  SmokeDetectorType,
  SystemType,
  WaterMeterType,
  WirelessMode,
  YesNo
} from "../../reducers/types";
import {useWizardNavigation} from "../../utils/app-utils";
import RadioButtons from "../common/RadioButtons";
import Tooltip from "../common/Tooltip";


/**
 * Wizard meter details step component.
 */
export const MeterDetails: FC<Partial<StepWizardChildProps>> = (props) => {
  const plan = useAppSelector<PlanState>((state) => state.plan)
  const dispatch = useAppDispatch()

  const [systemType, setSystemType] = useState<SystemType | "">(plan.systemType)
  const [wirelessMode, setWirelessMode] = useState<WirelessMode | "">(plan.wirelessMode)
  const [waterMeterType, setWaterMeterType] = useState<WaterMeterType | "">(plan.waterMeterType)
  const [meterSize, setMeterSize] = useState<MeterSize | "">(plan.meterSize)
  const [moreThanOneMeterPerApartment, setMoreThanOneMeterPerApartment] = useState<YesNo | "">(
    plan.moreThanOneMeterPerApartment
  )
  const [apartmentScreensEnabled, setApartmentScreensEnabled] = useState<YesNo | "">(plan.apartmentScreensEnabled)
  const [temperatureMetersEnabled, setTemperatureMetersEnabled] = useState<YesNo | "">(plan.temperatureMetersEnabled)
  const [smokeDetectorType, setSmokeDetectorType] = useState<SmokeDetectorType>(plan.smokeDetectorType)

  const navigation = useWizardNavigation(props)

  function onChangeMeterSize(newValue: MeterSize | ""): void {
    setMeterSize(newValue)
  }

  const isValid = () => {
    if (waterMeterType === '') {
      return false
    }
    if (moreThanOneMeterPerApartment === '') {
      return false
    }
    if (meterSize === '') {
      return false
    }
    if (apartmentScreensEnabled === '') {
      return false
    }
    if (systemType === '') {
      return false
    }
    if (systemType === 'wireless') {
      if (temperatureMetersEnabled === '') {
        return false
      }
      if (wirelessMode === '') {
        return false
      }
      // Hydrolink Air only supports meters of size dn15
      if (meterSize !== 'dn15' && wirelessMode === 'hydrolinkAir') {
        return false
      }
    }
    return true
  }
  const onProceed = async () => {
    dispatch(updatePlan({
      waterMeterType,
      moreThanOneMeterPerApartment,
      meterSize,
      apartmentScreensEnabled,
      systemType,
      temperatureMetersEnabled,
      wirelessMode,
      smokeDetectorType
    }))
    dispatch(resetReportState())
    await dispatch(generateProductList())
  }

  return (
    <div>
      <h4 className='mb-4'>Mittausjärjestelmän tiedot</h4>
      <div className='row'>
        <div className='col-12 col-md-8'>
          <label>Mittaukset *</label>
          <RadioButtons
            name='waterMeterType'
            value={waterMeterType}
            options={[
              {value: 'both', display: 'KV+LV'},
              {value: 'cold', display: 'KV'},
              {value: 'warm', display: 'LV'}
            ]}
            onChange={setWaterMeterType}
          />

          <label>Mittariparien lukumäärä per huoneisto *</label>
          <Tooltip title={'Jos huoneistojen kaikki vesi kulkee yksien mittareiden kautta, anna tähän arvoksi ' +
            '1. Jos taas kylphuoneen ja keittiön vedet tulevat eri nousuista, anna arvoksi 2.'}
          />

          {/* <label>Onko huoneistoissa useampia mittarointeja kuin 1 / huoneisto? *</label> */}
          <RadioButtons
            name='moreThanOneMeterPerApartment'
            value={moreThanOneMeterPerApartment}
            options={[
              {value: 'no', display: '1'},
              {value: 'yes', display: '2'}
            ]}
            onChange={setMoreThanOneMeterPerApartment}
          />

          <label>Mittarikoko *</label>
          <RadioButtons
            name='meterSize'
            value={meterSize}
            options={[
              {value: 'dn15', display: 'DN15'},
              {value: 'dn20', display: 'DN20'}
            ]}
            onChange={onChangeMeterSize}
          />

          <label>Huoneistonäytöt *</label>
          <RadioButtons
            name='apartmentScreensEnabled'
            value={apartmentScreensEnabled}
            options={[
              {value: 'yes', display: 'Kyllä'},
              {value: 'no', display: 'Ei'}
            ]}
            onChange={setApartmentScreensEnabled}
          />

          <label>Langallinen vai langaton järjestelmä? *</label>
          <RadioButtons
            name='systemType'
            value={systemType}
            options={[
              {value: 'wired', display: 'Langallinen'},
              {value: 'wireless', display: 'Langaton'}
            ]}
            onChange={(newSystemType) => {
              setSystemType(newSystemType)
              // Reset wireless mode (closely tied to the chosen system type, e.g. changing from wireless to wired
              // system type should reset the wireless mode too).
              setWirelessMode('')
              // Smoke detectors are only offered for Hydrolink Air, so reset the selection too.
              setSmokeDetectorType('none')
            }}
          />

          {
            systemType === 'wireless'
              ? <div className='card p-3 mb-3'>
                <h6 className='mb-2'>Langattoman vedenmittausjärjestelmän tiedot</h6>
                <label>Vedenmittausjärjestelmä</label>
                <RadioButtons
                  name='wirelessMode'
                  value={wirelessMode}
                  options={[
                    {
                      value: 'hydrolinkAir',
                      display: 'Hydrolink Air',
                      disabled: meterSize === 'dn20',
                      disabledReasonTooltip:
                        'Hydrolink Air valittavissa vain DN15 tyyppiselle mittarikoolle'
                    }
                  ]}
                  onChange={setWirelessMode}
                />
                <label>Lämpötilan- ja kosteudenmittaus *</label>
                <RadioButtons
                  name='temperatureMetersEnabled'
                  value={temperatureMetersEnabled}
                  options={[
                    {value: 'yes', display: 'Kyllä'},
                    {value: 'no', display: 'Ei'}
                  ]}
                  onChange={setTemperatureMetersEnabled}
                />

                {
                  wirelessMode === 'hydrolinkAir'
                    ? <>
                      <div>
                        <label>Palovaroittimet</label>
                        <Tooltip title={'Määrä on oletuksena yksi palovaroitin per asunto. Suurempiin asuntoihin ' +
                          'voidaan kuitenkin tarvita useampi palovaroitin.'}
                        />
                      </div>
                      <RadioButtons
                        name='smokeDetectorType'
                        value={smokeDetectorType}
                        options={[
                          {value: 'none', display: 'Ei'},
                          {value: 'mains', display: 'HYDROLINK SD-LR 230V (kiinteä sähkönsyöttö)'},
                          {value: 'battery', display: 'HYDROLINK SD-LR (paristotoiminen)'}
                        ]}
                        onChange={setSmokeDetectorType}
                      />
                    </>
                    : null
                }
              </div>
              : null
          }

        </div>
        <div className='d-none d-md-block col-4 text-center'>
          <i className='fas fa-tachometer-alt fa-10x mr-4' style={{color: '#d2ddec'}}/>
        </div>
      </div>
      <WizardNavigationButtons testIdPrefix='meter-details' isValid={isValid()} onProceed={onProceed}
                               navigation={navigation}/>
    </div>
  )
}
